// CORE
import React, { useEffect, useState, useRef, useCallback } from "react"

// TYPES
import { Children, TabType } from '../../custom-types'

interface InnerTemplateI {
  children?: Children
}

interface AccordionI extends InnerTemplateI {
  articleCount?: number
  content: TabType[]
  id?: string
  InnerTemplate?: JSX.Element
  formatAsHTML?: boolean
  enforceSingle?: boolean
  onOpened?: (string)=>void
  autoOpenFirst?: boolean
  itemRefs?: any
}

const InnerTemplate = ({children}: InnerTemplateI): JSX.Element => (<>{children}</>)

const Accordion = ({ children,  ...props}: AccordionI) => {
  const [content, setContent] = useState(props.content)
  const [itemHeights, setItemHeights] = useState(Array(content.length).fill(0))
  const articleCount = props.articleCount
  const groupId = props.id
  const itemRefs = props.itemRefs ?? useRef([])
  const toggle = (item:  TabType) => {
    const newContent = content.map((el: TabType, idx: number) => {
      if(el.title === item.title) {
        if(!el.isActive && props.onOpened) {
          props.onOpened(el.title);
        }

        el.isActive = props.enforceSingle ? true : !el.isActive
      }

      if(props.enforceSingle && el.title !== item.title){
        el.isActive = false;
      }

      itemHeights[idx] = el.isActive ? itemRefs.current[idx].children[1].scrollHeight : 0;

      return el
    });

    setItemHeights(itemHeights);
    setContent(newContent);
  }

  const getRef = (idx: number) => {
    return useCallback(node => {
      if (node !== null) {
        const resizeObserver = new ResizeObserver(() => { 
          if (itemHeights[idx] !== 0 && node.getBoundingClientRect().height !== 0) {
            itemHeights[idx] = node.getBoundingClientRect().height;
            setItemHeights(itemHeights);
            setContent(content.map((el: TabType) => { return el}));
          }
        });
        resizeObserver.observe(node);
      }
    }, []);
  }

  useEffect(()=> {
    if(props.autoOpenFirst && content.length){
      toggle(content[0]);
    }
  }, [])

   return (
    <dl role="presentation" className="accordion" >
      {content.map((item: TabType, index: number) => {
        return (
          <div key={index}  id={`${groupId}Item-${index}`}  className={`accordion-item ${item.isActive ? 'is-active' : ''}`} ref={el => (itemRefs.current[index] = el)}>
            <dt className="accordion-item-title">
              <button
                onClick={_event => toggle(item)}
                className={`accordion-item-trigger`}
                aria-expanded={item.isActive}
                aria-controls={`accordionDetails-${index}`}
                id={`accordionTitle-${index}-${groupId}`}
              >
                <p role="heading" aria-level={3} className="accordion-item-title-text">
                  {`${item.title}  `}
                  {articleCount && <span className="accordion-item-title-label">
                    { `${articleCount} article${articleCount && articleCount > 1 ? 's' : ''}` }
                  </span>}
                </p>
                  <svg
                    className="accordion-item-trigger-icon"
                    width="26"
                    height="14"
                    viewBox="0 0 26 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1L13 13L25 1"
                      stroke="black"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
              </button>
            </dt>
              <dd
                id={`accordionDetails-${index}`}
                role="region"
                aria-labelledby={`accordionTitle-${index}-${groupId}`}
                className="accordion-item-details"
                style={{height: itemHeights[index]}}
              >
                {props.formatAsHTML &&
                  <div className="accordion-item-details-inner">
                    {item.details}
                  </div>
                }
                {!props.formatAsHTML &&
                  <template >
                    <div className="accordion-item-details-inner">
                      { item.details }
                    </div>
                  </template>
                }
                <div ref={getRef(index)}>
                  { children }
                </div>
              </dd>
          </div>
          )
        })}
    </dl>
  )
}

Accordion.InnerTemplate = InnerTemplate

export default Accordion
